import './CategoryDetails.css';
import React, { useState } from 'react';
import { getCategory, deleteCategory, getCategoryName } from '../../model/category';
import { Card } from '../../components/Card/Card';
import { Container } from '../../components/Container/Container';
import { useRouterParam } from '../../model/history';
import { KeyValueTable } from '../../components/KeyValueTable/KeyValueTable';
import { getShopName } from '../../model/shop';
import { LoadingPage } from '../../components/LoadingPage/LoadingPage';
import { PermissionButton } from '../../components/Button/PermissionButton';
import { CategoryForm } from './CategoryForm';
import { useConfirmation } from '../../components/ConfirmationPrompt/ConfirmationPrompt';
import { useRemoteData } from '../../model/data';
import { formatDateTime } from '../../util/util';
import { t } from '../../locales/translate';
import ImagePreview from '../../components/ImagePreview/ImagePreview';
import { Icon } from '../../components/Icon/Icon';
import sortOrderExample from './../../assets/sort_order.jpg';
import sortOrderHomepageExample from './../../assets/sort_order_homepage.jpg';
import sortOrderTopLeft from './../../assets/sort_order_top_left.jpg';
import sortOrderTopRight from './../../assets/sort_order_top_right.jpg';
import sortOrderBottom from './../../assets/sort_order_bottom.jpg';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { Stack } from '../../components/Stack/Stack';
import { Modal } from '../../components/Modal/Modal';
import { Meta } from '../../components/meta/meta';

export function CategoryDetails(): JSX.Element {
  const categoryId = useRouterParam<{ id: string }>().id;
  const [showEditModal, setShowEditModal] = useState(false);

  const [category, loading, error] = useRemoteData(
    () => getCategory(categoryId),
    (db) => db.table('categories').where({ id: categoryId })[0],
    [categoryId]
  );

  const { confirm } = useConfirmation({
    title: t.actions.delete(t.model.category._name),
    message: t.general.confirmDelete(t.model.category._name),
    action: async () => await deleteCategory(categoryId),
  });

  if (loading) {
    return <LoadingPage title={t.actions.loading(t.model.category._name)} />;
  } else if (error || !category) {
    return <Container>{t.errors.itemNotFound(t.model.category._name)}.</Container>;
  }

  return (
    <Container>
      <Stack size="lg">
        <Card
          actions={
            category && <PermissionButton name="trash" model={category} permission="destroy" onClick={confirm} />
          }
        >
          <h1 className="category-details__header-name">{category.name}</h1>
          <div className="category-details__header-bottom ">
            <span className="category-details__header-slug">{category.slug}</span>
            {category.type}
          </div>
        </Card>
        <Card
          title={`${t.model.category._name} ${t.general.details}`}
          actions={
            category && (
              <PermissionButton
                name="edit"
                model={category}
                permission="update"
                onClick={() => setShowEditModal(true)}
              />
            )
          }
        >
          <KeyValueTable
            fullWidth={false}
            items={[
              {
                key: t.model.user.name,
                value: category.name,
              },
              { key: t.general.description, value: category.description, preserveLineBreaks: true },
              { key: t.general.subtitle, value: category.subtitle },
              {
                key: t.model.shop.subtitleInShop,
                value: (category.subtitleInShop && <Icon name="check" />) || undefined,
              },
              {
                key: t.model.category.altNames,
                value: category.alternativeNames && category.alternativeNames.join(', '),
              },
              {
                key: t.model.category.abstract,
                value: category.abstract,
              },
              {
                key: t.model.shop._name,
                value: category.shop?.name || getShopName(category.shopId),
              },
              {
                key: t.model.shop.parent,
                value: getCategoryName(category.parentCategory, { withShop: true }),
              },
              {
                key: t.model.category.mainCategory,
                value: category.mainCategory ? t.general.yes : t.general.no,
              },
              {
                key: t.model.category.featuredAt,
                value: formatDateTime(category.featuredAt),
              },
              {
                key: 'title',
                value: t.model.image._plural,
              },
              {
                key: t.model.image.square,
                value: <ImagePreview src={category.squareImage?.url} />,
                style: category.squareImage?.url
                  ? {
                      height: 110,
                    }
                  : undefined,
              },
              {
                key: t.model.image.portrait,
                value: <ImagePreview src={category.portraitImage?.url} />,
                style: category.portraitImage?.url
                  ? {
                      height: 110,
                    }
                  : undefined,
              },
              {
                key: t.model.image.landscape,
                value: <ImagePreview src={category.landscapeImage?.url} />,
                style: category.landscapeImage?.url
                  ? {
                      height: 110,
                    }
                  : undefined,
              },
              {
                key: 'title',
                value: t.model.tour.sortOrder,
              },
              {
                key: t.model.tour.sortOrder,
                keyNode: (
                  <>
                    <span>{t.model.tour.sortOrder} </span>
                    <Tooltip position="right-end" reference={<Icon name="question--sm" />}>
                      <div className="category-form__example-tooltip">
                        <p>{t.model.category.orderTooltip}</p>
                        <strong>{t.general.example}</strong>
                        <img className="category-form__sort-order-example" src={sortOrderExample} />
                      </div>
                    </Tooltip>
                  </>
                ),
                value: category.order,
              },
              {
                key: t.model.tour.sortOrderHomepage,
                keyNode: (
                  <>
                    <span>{t.model.tour.sortOrderHomepage} </span>
                    <Tooltip position="right-end" reference={<Icon name="question--sm" />}>
                      <div className="category-form__example-tooltip">
                        <p>{t.model.category.orderHomepageTooltip}</p>
                        <strong>{t.general.example}</strong>
                        <img className="category-form__sort-order-homepage-example" src={sortOrderHomepageExample} />
                      </div>
                    </Tooltip>
                  </>
                ),
                value: category.sortOrderHomepage,
              },
              {
                key: t.model.category.sortOrderFeatureTopLeft,
                keyNode: (
                  <>
                    <span>{t.model.category.sortOrderFeatureTopLeft} </span>
                    <Tooltip position="right-end" reference={<Icon name="question--sm" />}>
                      <div className="category-form__example-tooltip">
                        <p>{t.model.category.orderTicketTopLeftTooltip}</p>
                        <strong>{t.general.example}</strong>
                        <img className="category-form__sort-order-ticket-top-left-example" src={sortOrderTopLeft} />
                      </div>
                    </Tooltip>
                  </>
                ),
                value: category.sortOrderTicketTopLeft,
              },
              {
                key: t.model.category.sortOrderFeatureTopRight,
                keyNode: (
                  <>
                    <span>{t.model.category.sortOrderFeatureTopRight} </span>
                    <Tooltip position="right-end" reference={<Icon name="question--sm" />}>
                      <div className="category-form__example-tooltip">
                        <p>{t.model.category.orderTicketTopRightTooltip}</p>
                        <strong>{t.general.example}</strong>
                        <img className="category-form__sort-order-ticket-top-right-example" src={sortOrderTopRight} />
                      </div>
                    </Tooltip>
                  </>
                ),
                value: category.sortOrderTicketTopRight,
              },
              {
                key: t.model.category.sortOrderFeatureBottom,
                keyNode: (
                  <>
                    <span>{t.model.category.sortOrderFeatureBottom} </span>
                    <Tooltip position="right-end" reference={<Icon name="question--sm" />}>
                      <div className="category-form__example-tooltip">
                        <p>{t.model.category.orderTicketBottomTooltip}</p>
                        <strong>{t.general.example}</strong>
                        <img className="category-form__sort-order-ticket-bottom-example" src={sortOrderBottom} />
                      </div>
                    </Tooltip>
                  </>
                ),
                value: category.sortOrderTicketBottom,
              },
            ]}
          />
        </Card>
        <Meta id={category.id} type="category" />
      </Stack>
      <Modal open={showEditModal} title={t.actions.edit(t.model.category._name)} size="small">
        <CategoryForm
          model={{
            ...category,
            alternativeNames: category.alternativeNames?.join(','),
          }}
          onClose={() => setShowEditModal(false)}
        />
      </Modal>
    </Container>
  );
}
